// AnnualLeaveSettlement.jsx — 연차정산(미사용 연차 정산, 계산·정리 전용).
//   회사별 스코프(companyCode prop): PINE 베이스(/admin/leave-settlement) + 외부 ext/:code.
//   미사용 잔여 = 연차 관리와 동일. 통상일급 = 급여 결근공제 1일급(정산 기준일 시점). 정산액 = 미사용 × 통상일급.
//   "정산 처리" → leaves 정산행(잔여 차감). 급여는 안 건드림. 감사로그·이력 자동.
import React, { useCallback, useEffect, useState } from 'react';
import { apiClient } from '../../../api/apiClient';
import { useToast } from '../../../components/Toast';
import { useCompanyNames } from '../adminlayout/useCompanyNames';
import { todayKST } from '../../../utils/date';
import SettleModal from './SettleModal';
import {
  stdHeaderRow, stdPageTitle, stdPageSubtitle,
  stdBlackBtn, stdOutlineBtn,
  stdTableWrap, stdTable, stdTh, stdTd, stdCell,
} from '../adminlayout/designStandard';

const MOBILE_BREAKPOINT = 768;
// 회사 이름 = 작업 대상과 같은 이름(useCompanyNames) — 파인그룹·도트·LNJ 세 개만 박혀 있던 이름표 없앰(2026-10-28 'PINE' 정리 8-4)
// 정산일(=지급일)이 휴일인 경우 처리 — 급여 관리의 '지급일이 휴일인 경우'와 동일 체계
const HOLIDAY_RULES = [
  { value: 'prev_business_day', label: '직전 영업일로 당김' },
  { value: 'next_business_day', label: '다음 영업일로 연기' },
  { value: 'as_is', label: '변경 없음 (해당일 그대로)' },
];
const won = (n) => `${Math.round(Number(n) || 0).toLocaleString('ko-KR')}원`;

export default function AnnualLeaveSettlement({ companyCode = 'PINE' }) {
  const cc = String(companyCode || 'PINE').toUpperCase();
  const nameOf = useCompanyNames();
  const { showToast } = useToast();
  const [isMobile, setIsMobile] = useState(window.innerWidth <= MOBILE_BREAKPOINT);
  const [asOf, setAsOf] = useState(todayKST().slice(0, 10));
  const [candidates, setCandidates] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [settings, setSettings] = useState(null);
  const [savingSettings, setSavingSettings] = useState(false);

  // 정산 모달 상태 (입력값은 SettleModal 내부에서 관리)
  const [target, setTarget] = useState(null); // 정산 대상 후보
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    const q = `company_code=${encodeURIComponent(cc)}&as_of=${encodeURIComponent(asOf)}`;
    const [cRes, hRes, sRes] = await Promise.all([
      apiClient.get(`/api/admin/leave-settlement/candidates?${q}`),
      apiClient.get(`/api/admin/leave-settlement/history?company_code=${encodeURIComponent(cc)}`),
      apiClient.get(`/api/admin/leave-settlement/settings?company_code=${encodeURIComponent(cc)}`),
    ]);
    setLoading(false);
    if (cRes.ok && cRes.data?.success) setCandidates(cRes.data.data || []);
    else setError(cRes.data?.message || '후보 조회 실패');
    if (hRes.ok && hRes.data?.success) setHistory(hRes.data.data || []);
    if (sRes.ok && sRes.data?.success) setSettings(sRes.data.data || null);
  }, [cc, asOf]);

  async function saveSettings(rule) {
    setSavingSettings(true);
    const res = await apiClient.put('/api/admin/leave-settlement/settings', { company_code: cc, holiday_rule: rule });
    setSavingSettings(false);
    if (!res.ok || !res.data?.success) { showToast(res.data?.message || '설정 저장 실패', 'error'); return; }
    showToast('연차정산 설정을 저장했습니다.', 'success');
    await load();
  }

  useEffect(() => { load(); }, [load]);

  // 화면 크기 감지 (표준 헤더 여백용)
  useEffect(() => {
    function onResize() { setIsMobile(window.innerWidth <= MOBILE_BREAKPOINT); }
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  function openSettle(row) {
    setTarget(row);
  }

  // SettleModal 에서 정산일(자동값/달력)·일수·통상일급(수동)·사유를 받아 처리.
  async function submitSettle({ days, date, reason, dailyWage, amount, manual }) {
    if (!(days > 0)) { showToast('정산 일수를 확인하세요.', 'error'); return; }
    if (days > Number(target.remaining) + 1e-9) { showToast('미사용 연차보다 많이 정산할 수 없습니다.', 'error'); return; }
    if (!date) { showToast('정산일을 선택하세요.', 'error'); return; }
    if (!(dailyWage > 0)) { showToast('통상일급을 입력하세요.', 'error'); return; }
    setSaving(true);
    const finalReason = reason
      || `${target.settlement_type === 'resign' ? '퇴사 정산' : '미사용 연차 정산'} ${days}일 × 통상일급 ${won(dailyWage)} = ${won(amount)}`;
    const res = await apiClient.post('/api/admin/leave-settlement', {
      company_code: cc, user_id: target.user_id, days, date, reason: finalReason,
      ...(manual ? { daily_wage: dailyWage } : {}),
    });
    setSaving(false);
    if (!res.ok || !res.data?.success) { showToast(res.data?.message || '정산 처리 실패', 'error'); return; }
    showToast(`${target.name} 연차정산 완료 (${days}일, ${won(amount)})`, 'success');
    setTarget(null);
    await load();
  }

  const totalUnused = candidates.reduce((s, c) => s + (Number(c.remaining) > 0 ? Number(c.remaining) : 0), 0);
  const totalAmount = candidates.reduce((s, c) => s + (Number(c.settlement_amount) || 0), 0);

  return (
    <div>
      {/* ── 페이지 헤더: 제목/부제(좌) + 기준일·조회(우) — 직원 관리 표준 ── */}
      <div style={stdHeaderRow(isMobile)}>
        <div>
          <h2 style={stdPageTitle}>연차정산</h2>
          <div style={stdPageSubtitle}>미사용 연차를 정산합니다 · {nameOf(cc)} · 계산·정리 전용(급여 미반영)</div>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <label style={{ fontSize: 12, color: 'var(--text-muted)', fontWeight: 700 }}>정산 기준일</label>
          <input type="date" value={asOf} onChange={(e) => setAsOf(e.target.value)} style={input} />
          <button type="button" onClick={load} style={stdBlackBtn}>조회</button>
        </div>
      </div>

      <div style={page}>
      {error && <div style={errStyle}>{error}</div>}

      <div style={cardsRow}>
        <SummaryCard label="정산 대상(미사용>0)" value={`${candidates.filter((c) => Number(c.remaining) > 0).length}명`} />
        <SummaryCard label="미사용 연차 합" value={`${totalUnused.toLocaleString('ko-KR')}일`} />
        <SummaryCard label="예상 정산액 합" value={won(totalAmount)} strong />
      </div>

      <section style={card}>
        <div style={sectionHead}><h3 style={sectionTitle}>정산일 / 지급일 설정</h3></div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12, alignItems: 'end' }}>
          <div style={{ display: 'grid', gap: 4 }}>
            <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)' }}>정산일 기준</span>
            <div style={{ ...input, display: 'flex', alignItems: 'center', background: 'var(--bg-subtle)', color: 'var(--text-muted)' }}>
              입사기념일 지난 뒤 오는 급여일
            </div>
          </div>
          <div style={{ display: 'grid', gap: 4 }}>
            <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)' }}>급여 지급일 (급여 관리 설정)</span>
            <div style={{ ...input, display: 'flex', alignItems: 'center', background: 'var(--bg-subtle)', color: 'var(--text-muted)' }}>
              매월 {settings?.pay_day ?? '-'}일 · 지급일 = 정산일과 같은 날
            </div>
          </div>
          <div style={{ display: 'grid', gap: 4 }}>
            <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)' }}>정산일·지급일이 휴일인 경우</span>
            <select style={input} value={settings?.holiday_rule || 'prev_business_day'} disabled={savingSettings}
              onChange={(e) => saveSettings(e.target.value)}>
              {HOLIDAY_RULES.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
            </select>
          </div>
        </div>
        <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
          급여 지급일은 급여 관리에서 변경합니다. 휴일 규칙은 주말·공휴일·회사 휴일을 함께 반영합니다.
        </div>
      </section>

      <section style={sectionWrap}>
        <div style={sectionHead}><h3 style={sectionTitle}>정산 후보 <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-muted)' }}>· 다음 정산일 임박 순 (맨 위가 다음 정산 대상)</span></h3>
          <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{loading ? '조회 중…' : `${candidates.length}명`}</span></div>
        <div style={stdTableWrap}>
          <table style={table}>
            <thead><tr>
              <Th>직원</Th><Th>다음 정산일(=지급일)</Th><Th>입사기념일</Th><Th right>미사용</Th><Th right>통상일급</Th><Th right>정산액</Th><Th right>처리</Th>
            </tr></thead>
            <tbody>
              {candidates.length === 0 ? (
                <tr><td colSpan={7} style={emptyTd}>{loading ? '조회 중…' : '대상 직원이 없습니다.'}</td></tr>
              ) : candidates.map((c, i) => {
                const rem = Number(c.remaining) || 0;
                const cell = stdCell(i, candidates.length);
                return (
                  <tr key={c.user_id} style={rem > 0 ? undefined : { opacity: 0.55 }}>
                    <Td cell={cell}>
                      <strong>{c.name}</strong>
                      {c.settlement_type === 'resign' && <span style={resignBadge}>퇴사</span>}
                      {c.company_name ? <span style={muted}> · {c.company_name}</span> : null}
                    </Td>
                    <Td cell={cell}>
                      <strong>{c.next_settlement_date || '-'}</strong>
                      {c.overdue_days > 0
                        ? <span style={overdueBadge}>지연 {Number(c.overdue_days).toLocaleString('ko-KR')}일</span> : null}
                      {c.next_settlement_raw && c.next_settlement_raw !== c.next_settlement_date
                        ? <span style={muted}> (급여일 {c.next_settlement_raw} 휴일 조정)</span> : null}
                    </Td>
                    <Td cell={cell}>
                      {c.settlement_type === 'resign'
                        ? <>퇴사 {String(c.resign_date).slice(0, 10)}<span style={muted}> · 입사 {c.hire_date ? String(c.hire_date).slice(0, 10) : '-'}</span></>
                        : <>{c.next_anniv || '-'}<span style={muted}> · 입사 {c.hire_date ? String(c.hire_date).slice(0, 10) : '-'}</span></>}
                    </Td>
                    <Td cell={cell} right strong danger={rem < 0}>{rem.toLocaleString('ko-KR')}일</Td>
                    <Td cell={cell} right>{c.wage_note ? <span style={{ color: '#dc2626', fontSize: 11 }}>{c.wage_note}<br />(직접 입력)</span> : won(c.daily_wage)}</Td>
                    <Td cell={cell} right strong>{c.wage_note ? <span style={{ color: 'var(--text-muted)', fontWeight: 500 }}>-</span> : won(c.settlement_amount)}</Td>
                    <Td cell={cell} right>
                      <button type="button" disabled={!(rem > 0)} onClick={() => openSettle(c)}
                        style={{ ...settleBtn, opacity: rem > 0 ? 1 : 0.4, cursor: rem > 0 ? 'pointer' : 'not-allowed' }}>정산</button>
                    </Td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>

      <section style={sectionWrap}>
        <div style={sectionHead}><h3 style={sectionTitle}>정산 이력</h3>
          <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{history.length}건</span></div>
        <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: -4 }}>정산액은 이 메뉴로 정산한 시점의 금액을 고정 표시합니다(이후 급여가 바뀌어도 불변). 과거 수동 등록분은 금액이 기록되지 않아 "-" 로 표시됩니다.</div>
        <div style={stdTableWrap}>
          <table style={table}>
            <thead><tr>
              <Th>정산일</Th><Th>직원</Th><Th right>정산일수</Th><Th right>정산액</Th><Th>사유</Th><Th>처리자</Th>
            </tr></thead>
            <tbody>
              {history.length === 0 ? (
                <tr><td colSpan={6} style={emptyTd}>정산 이력이 없습니다.</td></tr>
              ) : history.map((h, i) => {
                const cell = stdCell(i, history.length);
                return (
                  <tr key={h.id}>
                    <Td cell={cell}>{h.date}</Td>
                    <Td cell={cell}><strong>{h.name}</strong>{h.department ? <span style={muted}> · {h.department}</span> : null}</Td>
                    <Td cell={cell} right strong>{Number(h.days).toLocaleString('ko-KR')}일</Td>
                    <Td cell={cell} right strong>{h.settlement_amount != null ? won(h.settlement_amount) : <span style={{ color: 'var(--text-muted)', fontWeight: 500 }}>-</span>}</Td>
                    <Td cell={cell}><span style={{ fontSize: 12 }}>{h.reason || '-'}</span></Td>
                    <Td cell={cell}>{h.created_by_name || '-'}</Td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>
      </div>

      {target && (
        <SettleModal
          key={target.user_id}
          target={target}
          saving={saving}
          onClose={() => setTarget(null)}
          onSubmit={submitSettle}
        />
      )}
    </div>
  );
}

function SummaryCard({ label, value, strong }) {
  return (
    <div style={{ border: '1px solid var(--border)', borderRadius: 8, background: 'var(--bg-card)', padding: 12, display: 'grid', gap: 4, minWidth: 150 }}>
      <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)' }}>{label}</span>
      <strong style={{ fontSize: strong ? 18 : 15, fontWeight: 850, color: 'var(--text)' }}>{value}</strong>
    </div>
  );
}
function Th({ children, right }) { return <th style={{ ...stdTh, textAlign: right ? 'right' : 'left' }}>{children}</th>; }
function Td({ children, right, strong, danger, cell = stdTd }) {
  return <td style={{ ...cell, textAlign: right ? 'right' : 'left', fontWeight: strong ? 800 : 500, color: danger ? '#dc2626' : 'var(--text)' }}>{children}</td>;
}

const page = { display: 'grid', gap: 12 };
const cardsRow = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 8 };
const card = { border: '1px solid var(--border)', borderRadius: 8, background: 'var(--bg-card)', padding: 12, display: 'grid', gap: 8, boxShadow: 'none' };
const sectionWrap = { display: 'grid', gap: 8 };
const sectionHead = { display: 'flex', justifyContent: 'space-between', alignItems: 'center' };
const sectionTitle = { margin: 0, fontSize: 14, color: 'var(--text)' };
const table = { ...stdTable, minWidth: 640 };
const emptyTd = { padding: 18, fontSize: 13, textAlign: 'center', color: 'var(--text-muted)', borderBottom: 'none' };
const muted = { color: 'var(--text-muted)', fontSize: 11 };
const input = { height: 32, padding: '0 8px', border: '1px solid var(--border)', borderRadius: 6, background: 'var(--bg-card)', color: 'var(--text)' };
const settleBtn = { ...stdOutlineBtn, padding: '5px 12px' };
const errStyle = { color: '#dc2626', fontSize: 13, border: '1px solid rgba(220,38,38,0.25)', background: 'rgba(220,38,38,0.08)', borderRadius: 6, padding: 10 };
const resignBadge = { marginLeft: 5, fontSize: 10, fontWeight: 700, padding: '1px 6px', borderRadius: 999, background: 'rgba(100,116,139,0.15)', color: '#475569', border: 'none', whiteSpace: 'nowrap' };
const overdueBadge = { marginLeft: 5, fontSize: 10, fontWeight: 700, padding: '1px 6px', borderRadius: 999, background: 'rgba(220,38,38,0.12)', color: '#dc2626', border: 'none', whiteSpace: 'nowrap' };
